import { FunctionComponent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon, LoadingBlock } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import {
	useCreateSalesforceFieldMappingMutation,
	useDeleteSalesforceFieldMappingMutation,
	useGetSalesforceAccountFieldsQuery,
	useGetSalesforceContactFieldsQuery,
	useGetSalesforceFieldMappingsQuery,
	useGetSalesforceLeadFieldsQuery,
	useGetSalesforceOpportunityFieldsQuery,
	useGetSalesforceSourceFieldsQuery,
	useTestSalesforceAiFieldMappingMutation,
	useUpdateSalesforceFieldMappingMutation,
} from "@/pages/Private/redux/salesforce/salesforce.api";
import { useLazyGetContactsQuery } from "@/pages/Private/redux/contact/contact.api";

import {
	FieldMappingAiConfig,
	FieldMappingSourceType,
	SalesforceEntityType,
	SalesforceField,
	SalesforceFieldMapping,
} from "../schema/salesforce";
import {
	NewSalesforceMapping,
	SalesforceMappingRow,
	SalesforceNewMappingRow,
} from "./SalesforceMappingRow";
import { SalesforceMappingPreviewModal } from "./SalesforceMappingPreviewModal";
import { SalesforceRestoreDefaultsButton } from "./SalesforceRestoreDefaultsButton";
import {
	SALESFORCE_TAB_ENTITY,
	SalesforceMappingTab,
	collectUsedSourceFields,
	collectUsedTargetFields,
	dealConfigReservedFieldsFor,
	restrictedValuesOf,
	sourceFieldsForTab,
	targetFieldsForTab,
} from "./salesforceFieldMapping.utils";
import {
	AiConfigModal,
	buildAiConfigLabels,
	buildAiProspectOptions,
	FieldMappingFooter,
} from "./fieldMapping";

interface SalesforceAiConfigSelection {
	id?: number;
	tempId?: string;
	label: string;
	targetField: SalesforceField;
	allFields: SalesforceField[];
	entityType: SalesforceEntityType;
	aiConfig?: FieldMappingAiConfig;
}

/**
 * The field-mapping surface itself — four sObject tabs, their rows, the AI-config modal and the
 * mapping preview.
 *
 * Rendered both as a settings tab and inside `SalesforceFieldMappingModal` (which the connection
 * tab opens), so it deliberately owns no chrome of its own.
 */
export const SalesforceFieldMappingPanel: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>): string =>
		t(`company.salesforce.fieldMapping.${key}`, options as never) as unknown as string;

	const [currentTab, setCurrentTab] = useState<SalesforceMappingTab>(SalesforceMappingTab.CONTACTS);
	const [newMappings, setNewMappings] = useState<NewSalesforceMapping[]>([]);
	const [savingTempId, setSavingTempId] = useState<string | null>(null);
	const [aiConfigTarget, setAiConfigTarget] = useState<SalesforceAiConfigSelection | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const aiProspectSearchRequestRef = useRef<{ abort?: () => void } | null>(null);

	const entityType = SALESFORCE_TAB_ENTITY[currentTab];

	const { data: contactMappings, isLoading: isLoadingContacts } =
		useGetSalesforceFieldMappingsQuery({ entityType: SalesforceEntityType.CONTACT });
	const { data: accountMappings, isLoading: isLoadingAccounts } =
		useGetSalesforceFieldMappingsQuery({ entityType: SalesforceEntityType.ACCOUNT });
	const { data: leadMappings, isLoading: isLoadingLeads } = useGetSalesforceFieldMappingsQuery({
		entityType: SalesforceEntityType.LEAD,
	});
	const { data: opportunityMappings, isLoading: isLoadingOpportunities } =
		useGetSalesforceFieldMappingsQuery({ entityType: SalesforceEntityType.OPPORTUNITY });

	const { data: contactFields } = useGetSalesforceContactFieldsQuery();
	const { data: accountFields } = useGetSalesforceAccountFieldsQuery();
	const { data: leadFields } = useGetSalesforceLeadFieldsQuery();
	const { data: opportunityFields } = useGetSalesforceOpportunityFieldsQuery();
	const { data: sourceFields } = useGetSalesforceSourceFieldsQuery();

	const [createMapping] = useCreateSalesforceFieldMappingMutation();
	const [updateMapping] = useUpdateSalesforceFieldMappingMutation();
	const [deleteMapping] = useDeleteSalesforceFieldMappingMutation();
	const [testAiMapping] = useTestSalesforceAiFieldMappingMutation();
	const [searchProspects] = useLazyGetContactsQuery();

	const isLoading =
		isLoadingContacts || isLoadingAccounts || isLoadingLeads || isLoadingOpportunities;

	const mappingsByTab: Record<SalesforceMappingTab, SalesforceFieldMapping[] | undefined> = {
		[SalesforceMappingTab.CONTACTS]: contactMappings,
		[SalesforceMappingTab.ACCOUNTS]: accountMappings,
		[SalesforceMappingTab.LEADS]: leadMappings,
		[SalesforceMappingTab.OPPORTUNITIES]: opportunityMappings,
	};

	const fieldsByTab: Record<SalesforceMappingTab, SalesforceField[]> = {
		[SalesforceMappingTab.CONTACTS]: contactFields?.fields ?? [],
		[SalesforceMappingTab.ACCOUNTS]: accountFields?.fields ?? [],
		[SalesforceMappingTab.LEADS]: leadFields?.fields ?? [],
		[SalesforceMappingTab.OPPORTUNITIES]: opportunityFields?.fields ?? [],
	};

	const currentTabNewMappings = useMemo(
		() => newMappings.filter((mapping) => mapping.entityType === entityType),
		[newMappings, entityType]
	);

	const usedSourceFields = useMemo(
		() => collectUsedSourceFields(mappingsByTab[currentTab], currentTabNewMappings),
		[mappingsByTab, currentTab, currentTabNewMappings]
	);

	const usedTargetFieldsByType = useMemo(
		() => collectUsedTargetFields(mappingsByTab[currentTab], currentTabNewMappings),
		[mappingsByTab, currentTab, currentTabNewMappings]
	);

	const handleAddMapping = () => {
		const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		setNewMappings((prev) => [
			...prev,
			{
				tempId,
				sourceField: "",
				sourceType: FieldMappingSourceType.FIELD,
				salesforceField: "",
				isActive: true,
				entityType,
			},
		]);
	};

	const handleUpdateNewMapping = (tempId: string, data: Partial<NewSalesforceMapping>) => {
		setNewMappings((prev) =>
			prev.map((mapping) => (mapping.tempId === tempId ? { ...mapping, ...data } : mapping))
		);
	};

	const handleConfigureAi = (
		mapping: {
			id?: number;
			tempId?: string;
			salesforceField: string;
			entityType: SalesforceEntityType | string;
			aiConfig?: FieldMappingAiConfig | null;
		},
		fields: SalesforceField[]
	) => {
		const target = fields.find((field) => field.name === mapping.salesforceField) ?? {
			name: mapping.salesforceField,
			label: mapping.salesforceField,
			type: "string",
		};

		setAiConfigTarget({
			id: mapping.id,
			tempId: mapping.tempId,
			label: target.label,
			targetField: target,
			allFields: fields,
			entityType: mapping.entityType as SalesforceEntityType,
			aiConfig: mapping.aiConfig ?? undefined,
		});
	};

	const handleSearchAiProspects = async (query: string) => {
		aiProspectSearchRequestRef.current?.abort?.();
		const request = searchProspects({ page: 1, limit: 10, searchValue: query });

		aiProspectSearchRequestRef.current = request;

		try {
			const result = await request.unwrap();

			return buildAiProspectOptions(result.data);
		} catch {
			return [];
		} finally {
			if (aiProspectSearchRequestRef.current === request) {
				aiProspectSearchRequestRef.current = null;
			}
		}
	};

	const handleSaveAiConfig = async (config: FieldMappingAiConfig) => {
		if (!aiConfigTarget) {
			return;
		}

		if (aiConfigTarget.tempId) {
			handleUpdateNewMapping(aiConfigTarget.tempId, {
				aiConfig: config,
				sourceField: aiConfigTarget.label,
			});
		} else if (aiConfigTarget.id) {
			await updateMapping({
				id: aiConfigTarget.id,
				data: {
					sourceType: FieldMappingSourceType.AI,
					sourceField: aiConfigTarget.label,
					aiConfig: config,
				},
			}).unwrap();
		}

		setAiConfigTarget(null);
	};

	const handleSaveNewMapping = async (mapping: NewSalesforceMapping) => {
		const isIncomplete =
			!mapping.salesforceField ||
			(mapping.sourceType !== FieldMappingSourceType.AI && !mapping.sourceField) ||
			(mapping.sourceType === FieldMappingSourceType.AI && !mapping.aiConfig?.prompt);

		if (isIncomplete) {
			return;
		}

		setSavingTempId(mapping.tempId);

		try {
			await createMapping({
				sourceField: mapping.sourceField || mapping.salesforceField,
				sourceType: mapping.sourceType,
				salesforceField: mapping.salesforceField,
				entityType: mapping.entityType as SalesforceEntityType,
				isActive: mapping.isActive,
				aiConfig: mapping.aiConfig,
			}).unwrap();
			setNewMappings((prev) => prev.filter((item) => item.tempId !== mapping.tempId));
		} catch (error) {
			console.error("Failed to save Salesforce mapping:", error);
		} finally {
			setSavingTempId(null);
		}
	};

	const tabItems = [
		{ key: SalesforceMappingTab.CONTACTS, label: ts("contacts") },
		{ key: SalesforceMappingTab.ACCOUNTS, label: ts("accounts") },
		{ key: SalesforceMappingTab.LEADS, label: ts("leads") },
		{ key: SalesforceMappingTab.OPPORTUNITIES, label: ts("opportunities") },
	];

	const targetFields = targetFieldsForTab(currentTab, fieldsByTab[currentTab]);
	const srcFields = sourceFieldsForTab(currentTab, sourceFields);

	const renderTabContent = () => {
		const mappings = mappingsByTab[currentTab];
		const aiCount =
			(mappings?.filter((mapping) => mapping.sourceType === FieldMappingSourceType.AI).length ??
				0) +
			currentTabNewMappings.filter((mapping) => mapping.sourceType === FieldMappingSourceType.AI)
				.length;
		const mappedCount = (mappings?.length ?? 0) + currentTabNewMappings.length;

		const reservedFields = dealConfigReservedFieldsFor(currentTab, fieldsByTab[currentTab]);

		return (
			<div className="mt-2">
				{reservedFields.length > 0 && (
					<div
						className="mt-2 rounded-lg bg-brand-white px-3 py-2 text-xs text-text-meta"
						data-testid="salesforce-reserved-fields-note"
					>
						{ts("reservedTooltip")}{" "}
						<span className="font-semibold text-text">
							{reservedFields.map((field) => field.label).join(", ")}
						</span>
					</div>
				)}
				<div className="flex items-center gap-3 border-b border-border-light py-3 text-sm font-medium text-text-meta">
					<div className="flex-1">{ts("ourField")}</div>
					<div className="w-[24px]"></div>
					<div className="flex-1">{ts("salesforceField")}</div>
					<div className="w-[60px] text-center">{ts("active")}</div>
					<div className="w-[80px]"></div>
				</div>

				{isLoading ? (
					<LoadingBlock className="py-8" />
				) : (
					<>
						{mappings?.map((mapping) => (
							<SalesforceMappingRow
								key={mapping.id}
								mapping={mapping}
								salesforceFields={targetFields}
								sourceFields={srcFields}
								ts={ts}
								onConfigureAi={(item) => handleConfigureAi(item, targetFields)}
								onDelete={(id) => deleteMapping(id)}
								onToggleActive={(id, isActive) => updateMapping({ id, data: { isActive } })}
							/>
						))}
						{currentTabNewMappings.map((mapping) => (
							<SalesforceNewMappingRow
								key={mapping.tempId}
								isSaving={savingTempId === mapping.tempId}
								mapping={mapping}
								salesforceFields={targetFields}
								sourceFields={srcFields}
								ts={ts}
								usedSourceFields={usedSourceFields}
								usedTargetFieldsByType={usedTargetFieldsByType}
								onCancel={(tempId) =>
									setNewMappings((prev) => prev.filter((item) => item.tempId !== tempId))
								}
								onConfigureAi={(item) => handleConfigureAi(item, targetFields)}
								onSave={handleSaveNewMapping}
								onUpdate={handleUpdateNewMapping}
							/>
						))}
						{!mappings?.length && !currentTabNewMappings.length && (
							<div className="py-6 text-center text-text-meta">{ts("noMappings")}</div>
						)}
					</>
				)}

				<div className="mt-4 flex flex-wrap items-center gap-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="mr-2 h-[16px] w-[16px] text-text" icon={faPlus} />}
						size={ButtonSize.S}
						testId="salesforce-add-mapping"
						title={ts("addMapping")}
						onClick={handleAddMapping}
					/>
					<SalesforceRestoreDefaultsButton entityType={entityType} ts={ts} />
				</div>

				<FieldMappingFooter
					aiCount={aiCount}
					creditPillLabel={ts("footerCreditPill")}
					mappedCount={mappedCount}
					previewLabel={ts("preview.button")}
					summaryLabel={ts("footerSummary", { mapped: mappedCount, ai: aiCount })}
					testIdPrefix="salesforce"
					onPreview={() => setIsPreviewOpen(true)}
				/>
			</div>
		);
	};

	return (
		<div className="p-4">
			<div className="mb-2 flex border-b border-border-light">
				{tabItems.map((tab) => (
					<button
						key={tab.key}
						className={classNames(
							"cursor-pointer border-b-2 px-4 py-2 text-sm font-medium transition-colors",
							currentTab === tab.key
								? "border-brand-light text-brand-light"
								: "border-transparent text-text-meta hover:border-border-light hover:text-text"
						)}
						data-testid={`salesforce-mapping-tab-${tab.key}`}
						type="button"
						onClick={() => setCurrentTab(tab.key)}
					>
						{tab.label}
					</button>
				))}
			</div>
			{renderTabContent()}

			<AiConfigModal
				allFields={aiConfigTarget?.allFields ?? []}
				callbacks={{
					onCancelSearchProspects: () => {
						aiProspectSearchRequestRef.current?.abort?.();
						aiProspectSearchRequestRef.current = null;
					},
					onSearchProspects: handleSearchAiProspects,
					onRunTest: (prospectId, prompt, fallbackValue) =>
						testAiMapping({
							prospectId,
							entityType: aiConfigTarget?.entityType ?? entityType,
							salesforceField: aiConfigTarget?.targetField.name,
							prompt,
							/**
							 * Only a **restricted** picklist constrains the value. An unrestricted one
							 * accepts and stores anything, so passing its options as a closed set would
							 * make the AI test reject a fallback the org would happily have taken.
							 */
							allowedValues: restrictedValuesOf(aiConfigTarget?.targetField),
							fallbackValue,
						}).unwrap(),
				}}
				initialConfig={aiConfigTarget?.aiConfig}
				isOpen={Boolean(aiConfigTarget)}
				labels={buildAiConfigLabels(ts)}
				subtitle={ts("aiConfigSubtitle", { label: aiConfigTarget?.label ?? "" })}
				targetField={aiConfigTarget?.targetField}
				title={ts("aiConfigTitle")}
				onClose={() => setAiConfigTarget(null)}
				onSave={handleSaveAiConfig}
			/>

			<SalesforceMappingPreviewModal
				entityType={entityType}
				isOpen={isPreviewOpen}
				salesforceFields={targetFields}
				sourceFields={srcFields}
				ts={ts}
				onCancelSearchProspects={() => {
					aiProspectSearchRequestRef.current?.abort?.();
					aiProspectSearchRequestRef.current = null;
				}}
				onClose={() => setIsPreviewOpen(false)}
				onSearchProspects={handleSearchAiProspects}
			/>
		</div>
	);
};
